import {AssistantRequestContext, AssistantScreen} from "@/ai-assistant/interfaces";

/**
 * What the server accepts for the view, in characters.
 *
 * The turn is refused whole when it is exceeded, so the label is cut rather
 * than sent as it stands: losing the end of « Planning · semaine du… » is a
 * small loss, losing the answer is not.
 */
const MAX_LENGTH = 120;

const SEPARATOR = ' · ';

/**
 * The screen as one line, the way the pharmacist would say it.
 *
 * « Planning · 14 sept – 19 sept 2026 · Claire ». One string rather than
 * separate fields because it lands in the prompt as prose — the model reads
 * « Vue en cours : … » and nothing parses it.
 */
export const screenLabel = (screen: AssistantScreen | null | undefined): string | null => {
    if (!screen) {
        return null;
    }

    const label = [screen.screen, screen.window, screen.collaborator]
        .map(part => part?.trim())
        .filter((part): part is string => !!part)
        .join(SEPARATOR);

    return label ? label.slice(0, MAX_LENGTH) : null;
};

/**
 * The screen as the request carries it, or nothing when there is no screen to
 * describe.
 *
 * An absent context and an empty one are not the same thing for the server:
 * one leaves « Vue en cours » out of the prompt, the other would state that
 * the pharmacist is looking at nothing.
 */
export const requestContext = (screen: AssistantScreen | null | undefined): AssistantRequestContext | undefined => {
    const view = screenLabel(screen);

    return view ? {view} : undefined;
};

/**
 * Whether an answer was computed on a screen other than the one the pharmacist
 * now has, among screens that say what they are.
 *
 * Compared on the label and not on the object: what matters is whether the
 * pharmacist would read the two as the same screen, and two objects describing
 * the same week are the same screen.
 *
 * A screen that declares nothing — a patient file, the home — is not a move.
 * Nothing is displayed there that an answer about last week could be taken
 * for, and saying so on every answer of the transcript would wear out the one
 * warning that matters: a planning screen showing another window.
 */
export const hasMoved = (
    asked: AssistantScreen | null | undefined,
    current: AssistantScreen | null | undefined
): boolean => {
    const from = screenLabel(asked);
    const to = screenLabel(current);

    return from !== null && to !== null && from !== to;
};
